<template>
  <div class="tg-page-root contact-page min-h-screen bg-gray-50 dark:bg-gray-950">
    <PageHeader :title="$t('PageTitle_Contact')" @back="goBack" />
    
    <main class="contact-content px-4 pt-4 pb-8">
      <section class="px-1 pb-1">
        <h1 class="text-xl font-bold leading-snug text-gray-900 dark:text-gray-100">{{ $t('help') }}</h1>
        <p class="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">{{ $t('teamSupport') }}</p>
      </section>

      <section class="mt-4 space-y-3">
        <button
          v-for="(item, index) in contactOptions"
          :key="item.channel"
          type="button"
          @click="navToContact(item.channel)"
          class="group flex w-full items-center gap-4 rounded-2xl border bg-white px-4 py-4 text-left shadow-sm transition-all active:scale-[0.99] dark:bg-gray-900 dark:shadow-none"
          :class="item.cardClass"
        >
          <div :class="item.iconWrapClass">
            <img :src="item.icon" class="h-6 w-6 object-contain" />
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="truncate text-base font-bold text-gray-900 dark:text-gray-100">{{ $t(item.titleKey) }}</h3>
            <p v-if="item.subtitleKey" class="mt-1 text-sm leading-5 text-gray-500 dark:text-gray-300">{{ $t(item.subtitleKey) }}</p>
          </div>
          <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition-colors group-hover:bg-white group-hover:text-brand dark:bg-gray-800 dark:text-gray-300 dark:group-hover:bg-gray-700 dark:group-hover:text-violet-300">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </section>

      <section class="mt-5 rounded-2xl border border-gray-100 bg-white/70 px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900/70 dark:shadow-none">
        <div class="flex items-center gap-3">
          <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 text-gray-400 dark:bg-gray-800 dark:text-gray-300">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <p class="truncate text-xs font-semibold text-gray-500 dark:text-gray-300">{{ $t('Contact_Business_Hours') }}</p>
            <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{{ $t('Contact_Company_Name') }} © {{ copyrightYear }}</p>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router'
import PageHeader from '@/components/PageHeader.vue'
import { useTelegramWebApp } from '@/composables/useTelegramWebApp'

const copyrightYear = new Date().getFullYear()

// Images
const tg_service = require('@/assets/icons/tg_service.png')
const tg = require('@/assets/icons/tg.png')
// const xiaohongshu = require('@/assets/icons/xiaohongshu.png')

const contactOptions = [
  {
    channel: 'tgSupportCustomerService1',
    titleKey: 'tgSupportCustomerService',
    subtitleKey: 'Contact_on_Telegram',
    icon: tg_service,
    cardClass: 'border-gray-100 hover:border-violet-100 hover:bg-violet-50/40 dark:border-gray-800 dark:hover:border-violet-500/30 dark:hover:bg-violet-500/5',
    iconWrapClass: 'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gray-50 transition-colors group-hover:bg-white dark:bg-gray-800 dark:group-hover:bg-gray-700'
  },
  {
    channel: 'tgGroup1',
    titleKey: 'tgGroup1',
    subtitleKey: 'Contact_Join_Community',
    icon: tg,
    cardClass: 'border-gray-100 hover:border-violet-100 hover:bg-violet-50/40 dark:border-gray-800 dark:hover:border-violet-500/30 dark:hover:bg-violet-500/5',
    iconWrapClass: 'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gray-50 transition-colors group-hover:bg-white dark:bg-gray-800 dark:group-hover:bg-gray-700'
  }
]

const router = useRouter()
const { openTelegramLink, openExternalLink } = useTelegramWebApp()

const TELEGRAM_CONTACT_CHANNELS = new Set([
  'tgSupportCustomerService1',
  'tgSupportCustomerService2',
  'tgGroup1',
  'tgGroup2',
])

const goBack = () => {
    router.back()
}

const navToContact = (channel) => {
    const channelUrls = {
        tgSupportCustomerService1: process.env.VUE_APP_TG_SUPPORT1,
        tgSupportCustomerService2: process.env.VUE_APP_TG_SUPPORT2,
        tgGroup1: process.env.VUE_APP_TG_GROUP1,
        tgGroup2: process.env.VUE_APP_TG_GROUP2,
        Xiaohongshu: process.env.VUE_APP_XIAOHONGSHU,
    }
    const url = channelUrls[channel]
    if (!url) return

    if (TELEGRAM_CONTACT_CHANNELS.has(channel)) {
      openTelegramLink(url)
      return
    }

    openExternalLink(url)
}
</script>

<style scoped>
:global(body.tg-fullscreen .contact-content) {
  padding-bottom: calc(2rem + var(--tg-content-safe-bottom, 0px));
}
</style>
